import { useCallback, useEffect, useMemo, useState } from 'react';

import useNoodleApi from '@/hooks/useNoodleApi';
import * as tsClient from '@/tsClient';
import { type SubmissionAIMessage, type SubmissionStatus } from '@/typings/integrations-api-models';

import { type FilingStep, type FilingStepStatus } from './types';

/** Meta-steps that represent the overall login/filing process, not user-visible filing steps. */
const FILTERED_STEP_NAMES = new Set(['login', 'filing']);

/**
 * Monotonic weight per status — processMessage only allows forward transitions
 * so that historical replay cannot regress a step already advanced by a live socket message.
 */
const STATUS_WEIGHT: Record<FilingStepStatus, number> = {
  pending: 0,
  active: 1,
  completed: 2,
  skipped: 2,
  failed: 2,
  cancelled: 2,
};

/** Canonical step display names matching the backend's stepProgress.ts definitions. */
const STEP_DISPLAY_NAMES: Record<string, string> = {
  caseUpload: 'Uploading case',
  ssnFiling: 'Uploading SSN Statement',
  ssnStatement: 'Uploading SSN Statement',
  payAdvices: 'Uploading Pay Advices',
  payAdvices2: 'Uploading Joint Debtor Pay Advices',
  incomeStatement: 'Uploading Income Statement',
  meansCalculation: 'Uploading Means Calculation',
  compensationDisclosure: 'Uploading Compensation Disclosure',
  creditCounseling: 'Uploading Credit Counseling Certificate',
  creditCounseling2: 'Uploading Joint Debtor Credit Counseling Certificate',
  chapter13Plan: 'Uploading Chapter 13 Plan',
  complianceCert: 'Uploading Compliance Certificate',
  creditorMatrix: 'Uploading Creditor Matrix',
  payment: 'Processing payment',
  assignTrustee: 'Assigning trustee',
  serviceAgreement: 'Uploading Service Agreement',
  domesticSupport: 'Uploading Domestic Support Obligation',
  signaturePages: 'Uploading Signature Pages',
};

/**
 * Get the display label for a step name. Uses the canonical backend name if known,
 * otherwise falls back to deriving a label from the camelCase step name.
 */
const getStepLabel = (name: string): string => {
  if (STEP_DISPLAY_NAMES[name]) return STEP_DISPLAY_NAMES[name];
  // Fallback for unknown steps: derive from camelCase
  return name
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/([a-zA-Z])(\d)/g, '$1 $2')
    .replace(/(\d)([a-zA-Z])/g, '$1 $2')
    .replace(/^./, (character) => character.toUpperCase());
};

type UseFilingProgressInput = {
  submissionAttemptId: string;
};

/** Return type for the useFilingProgress hook. */
type UseFilingProgressResult = {
  /** Ordered list of filing steps derived from backend messages. */
  steps: FilingStep[];
  /** Whether a cancel request is in-flight or awaiting backend confirmation. */
  isCancelling: boolean;
  /**
   * Process a WebSocket message — if it is a step-progress payload, update steps state.
   * Returns `true` if the message was a step-progress message (caller should filter from chat),
   * `false` otherwise.
   */
  processMessage: (message: SubmissionAIMessage) => boolean;
  /** Request cancellation of the filing. */
  handleCancel: () => Promise<void>;
  /** Mark the filing as finished, resetting cancelling state and updating step statuses if cancelled. */
  markFinished: (outcome: SubmissionStatus) => void;
  /** Set an error message on a specific step by name. */
  setStepError: (stepName: string, errorMessage: string) => void;
};

/**
 * Custom hook that parses step-progress JSON messages from WebSocket,
 * accumulates filing step state, and exposes a cancel handler.
 */
const useFilingProgress = ({ submissionAttemptId }: UseFilingProgressInput): UseFilingProgressResult => {
  const [stepsMap, setStepsMap] = useState<Map<string, FilingStep>>(() => new Map());
  const [isCancelling, setIsCancelling] = useState(false);

  const { getData: cancelSubmission } = useNoodleApi(tsClient.integrations.cancelCaseSubmission, {
    toastOnError: true,
  });

  /** Reset step state when the submission attempt changes to prevent stale steps from persisting. */
  useEffect(() => {
    if (!submissionAttemptId) return;
    setStepsMap(new Map());
    setIsCancelling(false);
  }, [submissionAttemptId]);

  /** Ordered array of filing steps derived from the map. */
  const steps = useMemo<FilingStep[]>(() => Array.from(stepsMap.values()), [stepsMap]);

  /**
   * Process a submission AI message. If it contains a step-progress JSON payload,
   * update the steps map and return true. Otherwise return false.
   */
  const processMessage = useCallback((message: SubmissionAIMessage): boolean => {
    if (!message.text) return false;

    let parsed: { type?: string; step?: string; status?: FilingStepStatus };
    try {
      parsed = JSON.parse(message.text) as { type?: string; step?: string; status?: FilingStepStatus };
    } catch {
      return false;
    }

    if (parsed.type !== 'step-progress' || !parsed.step || !parsed.status) {
      return false;
    }

    const stepName = parsed.step;
    const stepStatus = parsed.status;

    if (FILTERED_STEP_NAMES.has(stepName)) {
      return true;
    }

    const label = getStepLabel(stepName);

    setStepsMap((previous) => {
      const existing = previous.get(stepName);
      if (existing && STATUS_WEIGHT[stepStatus] < STATUS_WEIGHT[existing.status]) {
        return previous;
      }
      const next = new Map(previous);
      next.set(stepName, { label, name: stepName, status: stepStatus });
      return next;
    });

    return true;
  }, []);

  /** Request cancellation of the current filing submission. */
  const handleCancel = useCallback(async (): Promise<void> => {
    if (!submissionAttemptId || isCancelling) return;
    setIsCancelling(true);
    const { error } = await cancelSubmission({ id: submissionAttemptId });
    if (error) {
      setIsCancelling(false);
    }
    // On success, stay in cancelling state until backend confirms via markFinished
  }, [cancelSubmission, isCancelling, submissionAttemptId]);

  /**
   * Mark the filing as finished. Resets cancelling state unconditionally.
   * If the outcome is 'cancelled', sets all active/pending steps to cancelled.
   */
  const markFinished = useCallback((outcome: SubmissionStatus): void => {
    setIsCancelling(false);

    if (outcome === 'cancelled') {
      setStepsMap((previous) => {
        const next = new Map(previous);
        next.forEach((step, key) => {
          if (step.status === 'active' || step.status === 'pending') {
            next.set(key, { ...step, status: 'cancelled' });
          }
        });
        return next;
      });
    }
  }, []);

  /** Set an error message on a specific step identified by its name. */
  const setStepError = useCallback((stepName: string, errorMessage: string): void => {
    setStepsMap((previous) => {
      const existing = previous.get(stepName);
      if (!existing) return previous;
      const next = new Map(previous);
      next.set(stepName, { ...existing, errorMessage });
      return next;
    });
  }, []);

  return { handleCancel, isCancelling, markFinished, processMessage, setStepError, steps };
};

export default useFilingProgress;
